/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useRef, useState } from 'react'

import { useMediaQuery } from 'marketing-site/src/library/utils'
import { loadImage } from '../../utils/network'
import { ImagePlaceholder } from '../Image'
import { IProps } from './index'
import inSnapshotEnvironment from '../../utils/inSnapshotEnvironment'
import classNames from 'classnames'
import dynamic from 'next/dynamic'

const LottieVideo = dynamic(async () => (await import('../LottieVideo')).LottieVideo)

export function VideoAutoplay({
  video,
  mobileVideo,
  videoWebm,
  mobileVideoWebm,
  fallbackImage,
  playOnce,
  autoplay,
  displayFallbackOnTop,
  muted,
  showControls,
  forcedAnimationState,
  forcePoster,
  onCompleteCallback,
  getRef,
  noLazyLoad = false,
}: IProps): JSX.Element {
  const isSmall = useMediaQuery('(max-width: 768px)')
  const shouldAutoplay = autoplay === undefined ? true : autoplay
  const [hideVideo, setHideVideo] = useState(typeof fallbackImage !== 'string')
  const [isVisible, ref] = useIsElementVisible<HTMLVideoElement>()
  const fallbackImageSrc = typeof fallbackImage === 'string' ? fallbackImage : fallbackImage.url
  const lottieAnimation = video.match(/\.json$/) ? video : undefined

  const isMuted = muted === undefined ? true : muted

  useEffect(() => {
    !lottieAnimation && loadImage(fallbackImageSrc).then(() => setHideVideo(false))
  }, [fallbackImageSrc, lottieAnimation])

  useEffect(() => {
    if (!ref.current || !shouldAutoplay) return

    if (isVisible) {
      ref.current.play()
    } else {
      ref.current.pause()
    }
  }, [isVisible, ref, shouldAutoplay])

  const play = () => {
    if (!ref.current) return
    ref.current.play()
  }

  const replay = () => {
    if (!ref.current) return
    ref.current.pause()
    ref.current.currentTime = 0
    ref.current.play()
  }

  const pause = () => {
    if (!ref.current) return
    ref.current.pause()
  }

  const stop = () => {
    if (!ref.current) return
    const currentVideo = ref.current
    const isPlaying =
      currentVideo.readyState > currentVideo.HAVE_CURRENT_DATA &&
      !currentVideo.paused &&
      !currentVideo.ended &&
      currentVideo.currentTime > 0

    if (isPlaying) {
      ref.current.pause()
      ref.current.currentTime = 0
    }
  }

  if (forcedAnimationState === 'pause') {
    pause()
  }
  if (forcedAnimationState === 'stop') {
    stop()
  }
  if (forcedAnimationState === 'play') {
    play()
  }

  if (forcedAnimationState === 'replay') {
    replay()
  }

  const regularVideo = (
    <>
      <video
        controls={showControls}
        className="video-player"
        preload="metadata"
        autoPlay={shouldAutoplay}
        loop={!playOnce}
        muted={isMuted}
        playsInline
        key={isSmall && mobileVideo ? mobileVideo : video}
        poster={!shouldAutoplay && displayFallbackOnTop !== false ? fallbackImageSrc : undefined}
        onLoadedData={() => {
          setHideVideo(false)
          getRef && getRef(ref)
        }}
        onEnded={onCompleteCallback}
        ref={ref}
      >
        {videoWebm && (
          <source
            type="video/webm"
            src={isSmall && mobileVideoWebm ? mobileVideoWebm : videoWebm}
          />
        )}
        <source src={isSmall && mobileVideo ? mobileVideo : video} />
      </video>
      <style jsx>{`
        video {
          display: block;
          max-width: 100%;
        }
      `}</style>
    </>
  )

  const videoWrapperPlaceholderClassNames = classNames('video-player-wrapper__video-placeholder', {
    'video-player-wrapper__video-placeholder--hidden': hideVideo || inSnapshotEnvironment(),
  })

  return (
    <div className="video-player-wrapper">
      <div className="video-player-wrapper__video">
        {lottieAnimation ? (
          <LottieVideo
            animationUrl={lottieAnimation}
            placeHolder={fallbackImage}
            forcedAnimationState={forcedAnimationState}
            forcePoster={forcePoster}
            loop={!playOnce}
            onAnimationReady={() => setHideVideo(false)}
            onCompleteCallback={onCompleteCallback}
            getRef={getRef}
            noLazyLoad={noLazyLoad}
          />
        ) : (
          regularVideo
        )}
      </div>

      <div className={videoWrapperPlaceholderClassNames}>
        {typeof fallbackImage !== 'string' ? (
          <ImagePlaceholder
            altText={fallbackImage.altText}
            width={fallbackImage.width!}
            height={fallbackImage.height!}
          />
        ) : null}
      </div>

      <style jsx>
        {`
          .video-player-wrapper {
            display: grid;
            grid-template-columns: 1fr;
            position: relative;
            line-height: 0;
            border-radius: 25px;
            overflow: hidden;
            vertical-align: middle;

            &__video {
              grid-row-start: 1;
              grid-column-start: 1;
            }

            &__video-placeholder {
              grid-row-start: 1;
              grid-column-start: 1;
              opacity: 1;

              &--hidden {
                opacity: 0;
              }
            }
          }
        `}
      </style>
    </div>
  )
}

function useIsElementVisible<T extends Element>(): [boolean, React.MutableRefObject<T | null>] {
  const ref = useRef<T | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (!ref.current) return
    if (typeof window.IntersectionObserver === 'undefined') return
    const target = ref.current

    const visibilityChanged: IntersectionObserverCallback = function (entries) {
      entries.forEach((entry) => {
        setIsVisible(entry.isIntersecting)
      })
    }

    const observer = new IntersectionObserver(visibilityChanged)
    observer.observe(target)
    return () => observer.unobserve(target)
  })

  return [isVisible, ref]
}
